import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import { getNewsArticle, createNewsArticle, updateNewsArticle } from 'actions/admin/newsArticles';
import { handleResponse } from 'actions/actionHelpers';
import { convertToNumber } from 'helpers/StringHelper';
import { getLongDateFormat } from 'helpers/DateHelper';
import routes from 'routes';
import { required } from 'form/validation';
import { createTiptapEditor } from 'form/tiptapUtil';

import AdminContentWrapper from '../../presentational/AdminContentWrapper';
import AdminFormButtons from '../../presentational/AdminFormButtons';
import AdminFormFields from '../../presentational/AdminFormFields';
import AdminFormWrapper from '../../presentational/AdminFormWrapper';
import AdminPage from '../../presentational/AdminPage';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Loader from 'common/Loader';
import MarginBox from 'common/MarginBox';
import { Link } from 'react-router-dom';
import Form from 'form/Form';
import TiptapEditor from 'form/TiptapEditor';
import { TextField, showErrorOnBlur } from 'mui-rff';
import DatePicker from 'form/DatePicker';

const NewsArticle = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const params = useParams();

    const [newsArticle, setNewsArticle] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialValues, setInitialValues] = useState(undefined);
    const { newsArticleId } = params;

    const leadParagraphEditor = createTiptapEditor();
    const bodyEditor = createTiptapEditor();

    useEffect(() => {
        if(convertToNumber(newsArticleId)) {
            loadNewsArticle();
        } else {
            setNewsArticle({
                publicationDate: new Date()
            });
            setIsLoading(false);
        }
    }, [newsArticleId]);

    useEffect(() => {
        if(newsArticle) {
            setInitialValues({
                title: newsArticle.title,
                publicationDate: newsArticle.publicationDate
            });
            leadParagraphEditor.commands.setContent(newsArticle.leadParagraph);
            bodyEditor.commands.setContent(newsArticle.body);
        }
    }, [newsArticle]);

    const loadNewsArticle = () => {
      setIsLoading(true);
      dispatch(getNewsArticle(newsArticleId))
          .then(handleResponse(
              response => {
                  setNewsArticle(response.payload);
                  setIsLoading(false);
              },
              () => setIsLoading(false)
          ));
    };

    const handleFormSubmit = formValues => {
        setIsSubmitting(true);
        const request = {
            title: formValues.title,
            publicationDate: formValues.publicationDate,
            leadParagraph: leadParagraphEditor.getHTML(),
            body: bodyEditor.getHTML()
        };
        const action = convertToNumber(newsArticleId)
            ? updateNewsArticle(newsArticleId, request)
            : createNewsArticle(request);
        dispatch(action)
            .then(handleResponse(
                () => {
                    history.push(routes.admin.newsArticles);
                    setIsSubmitting(false);
                    return { snackbar: { message: 'News article was successfully saved.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const title = 'News article: ' + (newsArticle ? (newsArticle.title ?? 'New article') : 'Loading...');

    return (
        <AdminPage title={title}>
            {
                isLoading && <Loader />
            }
            {
                !isLoading && newsArticle && (
                    <AdminContentWrapper type="detail">
                        <AdminFormWrapper isSubmitting={isSubmitting}>
                            <Form
                                initialValues={initialValues}
                                onSubmit={handleFormSubmit}
                            >
                                {({ handleSubmit, invalid }) => (
                                    <form onSubmit={handleSubmit}>
                                        <AdminFormFields>
                                            <TextField
                                                name="title"
                                                variant="outlined"
                                                label="Title"
                                                required
                                                fieldProps={{ validate: required }}
                                            />

                                            <DatePicker
                                                name="publicationDate"
                                                label="Publication date"
                                                formatFunc={getLongDateFormat}
                                                inputVariant="outlined"
                                                showError={showErrorOnBlur}
                                                fieldProps={{ validate: required }}
                                            />

                                            <MarginBox bottom={2}>
                                                <Typography variant="body1" gutterBottom>Lead paragraph</Typography>
                                                <TiptapEditor editor={leadParagraphEditor} />
                                            </MarginBox>

                                            <Typography variant="body1" gutterBottom>Body</Typography>
                                            <TiptapEditor editor={bodyEditor} />
                                        </AdminFormFields>
                                        <AdminFormButtons>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                disabled={invalid}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                component={Link}
                                                to={routes.admin.newsArticles}
                                                variant="outlined"
                                                color="primary"
                                            >
                                                Back
                                            </Button>
                                        </AdminFormButtons>
                                    </form>
                                )}
                            </Form>
                        </AdminFormWrapper>
                    </AdminContentWrapper>
                )
            }
        </AdminPage>
    );
};

export default NewsArticle;
